import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';
import { TableSkeleton } from '@/components/ui/Skeletons';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

const FinishedGoods = ({ user }) => {
  const [goods, setGoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoods, setEditingGoods] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState('product_code');
  const [sortDir, setSortDir] = useState('asc');

  // Confirmation Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <ArrowUpDown size={14} className="ml-1 inline opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp size={14} className="ml-1 inline text-[hsl(145,60%,30%)]" />
      : <ArrowDown size={14} className="ml-1 inline text-[hsl(145,60%,30%)]" />;
  };
  const [formData, setFormData] = useState({
    product_code: '',
    name: '',
    current_stock: '',
    unit: '',
  });

  useEffect(() => {
    fetchGoods();
  }, []);

  const fetchGoods = async () => {
    try {
      const response = await axios.get(`${API}/finished-goods`);
      setGoods(response.data);
    } catch (error) {
      toast.error('Failed to fetch finished goods');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      product_code: '',
      name: '',
      current_stock: '',
      unit: '',
    });
    setEditingGoods(null);
  };

  const handleEdit = (good) => {
    setEditingGoods(good);
    setFormData({
      product_code: good.product_code,
      name: good.name,
      current_stock: good.current_stock,
      unit: good.unit,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        current_stock: parseFloat(formData.current_stock),
      };

      if (editingGoods) {
        await axios.put(`${API}/finished-goods/${editingGoods.id}`, data);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/finished-goods`, data);
        toast.success('Product created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchGoods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    }
  };

  const handleDeleteClick = (goods) => {
    setItemToDelete(goods);
    setConfirmOpen(true);
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/finished-goods/${itemToDelete.id}`);
      toast.success('Product deleted successfully');
      fetchGoods();
      setConfirmOpen(false);
    } catch (error) {
      toast.error('Failed to delete product');
    } finally {
      setIsDeleting(false);
      setItemToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="flex justify-between mb-8">
          <div>
            <div className="h-10 w-48 bg-stone-200/60 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-stone-200/60 rounded animate-pulse"></div>
          </div>
          <div className="h-10 w-32 bg-stone-200/60 rounded animate-pulse"></div>
        </div>
        <div className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm">
          <TableSkeleton rows={5} columns={5} />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="finished-goods-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-4xl font-semibold text-[hsl(145,60%,10%)] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Finished Goods</h1>
          <p className="text-sm text-[hsl(145,20%,40%)] mt-2">Track your finished product inventory</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white" data-testid="add-goods-button">
              <Plus size={16} className="mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingGoods ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="goods-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="product_code">Product Code</Label>
                  <Input
                    id="product_code"
                    value={formData.product_code}
                    onChange={(e) => setFormData({ ...formData, product_code: e.target.value })}
                    required
                    data-testid="input-product-code"
                  />
                </div>
                <div>
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="input-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="current_stock">Current Stock</Label>
                  <Input
                    id="current_stock"
                    type="number"
                    step="0.01"
                    value={formData.current_stock}
                    onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                    required
                    data-testid="input-current-stock"
                  />
                </div>
                <div>
                  <Label htmlFor="unit">Unit</Label>
                  <Input
                    id="unit"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    placeholder="box, set, pcs"
                    required
                    data-testid="input-unit"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white" data-testid="submit-goods-button">
                  {editingGoods ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(145,20%,40%)]" size={18} />
          <Input
            placeholder="Search by code or name..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="pl-10"
          />
        </div>
      </div>

      <div className="bg-white rounded-sm border border-stone-200 shadow-sm overflow-hidden mb-4">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full" data-testid="goods-table">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider cursor-pointer select-none hover:text-stone-900" onClick={() => toggleSort('product_code')}>Product Code <SortIcon column="product_code" /></th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider cursor-pointer select-none hover:text-stone-900" onClick={() => toggleSort('name')}>Name <SortIcon column="name" /></th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider cursor-pointer select-none hover:text-stone-900" onClick={() => toggleSort('current_stock')}>Current Stock <SortIcon column="current_stock" /></th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider cursor-pointer select-none hover:text-stone-900" onClick={() => toggleSort('unit')}>Unit <SortIcon column="unit" /></th>
                <th className="px-6 py-3 text-left text-xs font-bold text-stone-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {(() => {
                const filtered = goods.filter(g => {
                  if (!searchTerm) return true;
                  const q = searchTerm.toLowerCase();
                  return g.product_code.toLowerCase().includes(q) || g.name.toLowerCase().includes(q);
                });
                const sorted = [...filtered].sort((a, b) => {
                  let aVal = a[sortKey], bVal = b[sortKey];
                  if (sortKey === 'current_stock') {
                    aVal = Number(aVal) || 0; bVal = Number(bVal) || 0;
                  } else {
                    aVal = String(aVal || '').toLowerCase(); bVal = String(bVal || '').toLowerCase();
                  }
                  if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
                  if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
                  return 0;
                });
                const paginated = sorted.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
                return (
                  <>
                    {paginated.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-sm text-[hsl(145,20%,40%)]">No finished goods found</td>
                      </tr>
                    ) : (
                      paginated.map((good) => (
                        <tr key={good.id} className="hover:bg-stone-50/50 transition-colors" data-testid={`goods-row-${good.product_code}`}>
                          <td className="px-6 py-2 text-sm text-[hsl(145,60%,10%)]">{good.product_code}</td>
                          <td className="px-6 py-2 text-sm text-[hsl(145,60%,10%)]">{good.name}</td>
                          <td className="px-6 py-2 text-sm text-[hsl(145,60%,10%)]">{good.current_stock}</td>
                          <td className="px-6 py-2 text-sm text-[hsl(145,20%,40%)]">{good.unit}</td>
                          <td className="px-6 py-2 text-sm">
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(good)} className="h-8 w-8 p-0 text-stone-400 hover:text-[hsl(145,60%,30%)] hover:bg-[hsl(145,60%,95%)]" data-testid={`edit-goods-${good.product_code}`}>
                                <Edit size={14} />
                              </Button>
                              {user?.role === 'admin' && (
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(good)} className="h-8 w-8 p-0 text-stone-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-goods-${good.product_code}`}>
                                  <Trash2 size={14} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden flex flex-col divide-y divide-stone-100">
          {(() => {
            const filteredGoods = goods.filter(g => {
              if (!searchTerm) return true;
              const q = searchTerm.toLowerCase();
              return g.product_code.toLowerCase().includes(q) || g.name.toLowerCase().includes(q);
            });
            const sortedGoods = [...filteredGoods].sort((a, b) => {
              let aVal = a[sortKey], bVal = b[sortKey];
              if (sortKey === 'current_stock') {
                aVal = Number(aVal) || 0; bVal = Number(bVal) || 0;
              } else {
                aVal = String(aVal || '').toLowerCase(); bVal = String(bVal || '').toLowerCase();
              }
              if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
              if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
              return 0;
            });
            const paginatedGoods = sortedGoods.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

            if (paginatedGoods.length === 0) {
              return <div className="p-8 text-center text-[hsl(145,20%,40%)]">No finished goods found</div>;
            }

            return paginatedGoods.map((good) => (
              <div key={`mobile-${good.id}`} className="p-4 bg-white">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold text-stone-900">{good.name}</h4>
                    <p className="text-sm text-stone-500 font-mono mt-0.5">{good.product_code}</p>
                  </div>
                  <div className="text-right">
                    <span className="block text-lg font-bold text-stone-800">{good.current_stock}</span>
                    <span className="text-xs text-stone-500">{good.unit}</span>
                  </div>
                </div>

                <div className="flex gap-2 justify-end mt-4 pt-3 border-t border-stone-100">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => handleEdit(good)}>
                    <Edit size={14} className="text-stone-600" />
                  </Button>
                  {user?.role === 'admin' && (
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0 border-red-200 hover:bg-red-50" onClick={() => handleDeleteClick(good)}>
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            ));
          })()}
        </div>
      </div>
      <div className="flex justify-center">
        <Pagination currentPage={currentPage} totalItems={goods.filter(g => { if (!searchTerm) return true; const q = searchTerm.toLowerCase(); return g.product_code.toLowerCase().includes(q) || g.name.toLowerCase().includes(q); }).length} onPageChange={setCurrentPage} />
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={executeDelete}
        title={`Delete Product: ${itemToDelete?.name}?`}
        description="This action cannot be undone. This product will be removed from your catalog."
        confirmText="Delete Product"
        isLoading={isDeleting}
      />
    </div >
  );
};

export default FinishedGoods;